import React from "react";
import { AlertTriangle } from "lucide-react";
import CitedExcelCell from "./CitedExcelCell";
import DerivedCellTrace from "./DerivedCellTrace";
import type {
	CitedTableArtifact,
	CoverageStatus,
} from "../types/citationArtifact";
import {
	buildValueIndex,
	MISSING_PLACEHOLDER,
	presentCell,
	resolveCell,
} from "../utils/citationArtifact";
import { EvidencePeekProvider } from "./EvidencePeek/EvidencePeekContext";
import { isEvidencePeekEnabled } from "../utils/featureFlags";

interface CitedTableArtifactViewProps {
	artifact: CitedTableArtifact;
	chatId?: string | null;
}

const COVERAGE_LABEL: Record<CoverageStatus, string> = {
	covered: "All values verified",
	partial: "Partially verified",
	failed: "Verification failed",
};

const COVERAGE_TONE: Record<CoverageStatus, string> = {
	covered: "text-text-muted",
	partial: "text-amber-600",
	failed: "text-red-600",
};

/**
 * Renders a typed `CitedTableArtifact` directly — never via model-authored
 * markdown. Verified Excel cells are clickable; unverified cells are flagged;
 * missing cells show a placeholder. Layout (rows/columns) is decoupled from
 * data identity (slots/cells), so this reads through the slot indirection.
 */
const CitedTableArtifactView: React.FC<CitedTableArtifactViewProps> = ({
	artifact,
	chatId,
}) => {
	const { coverage } = artifact;
	const warnings = artifact.warnings ?? [];
	// Index by value_id once so derived cells can resolve their input leaves.
	const valueIndex = React.useMemo(() => buildValueIndex(artifact), [artifact]);
	const peekEnabled = isEvidencePeekEnabled();
	const content = (
		<div className="my-2 w-full overflow-x-auto">
			{artifact.title && (
				<div className="mb-1 text-base font-medium text-text-base">
					{artifact.title}
				</div>
			)}

			<table className="w-auto border-collapse text-base">
				<thead>
					<tr>
						{artifact.columns.map((col) => (
							<th
								key={col.column_key}
								className={`whitespace-nowrap border-b border-gray-200 px-3 py-2 text-sm font-medium text-text-muted ${
									col.kind === "dimension"
										? "sticky left-0 z-10 bg-white text-left"
										: "text-right"
								}`}
							>
								{col.label}
							</th>
						))}
					</tr>
				</thead>
				<tbody>
					{artifact.rows.map((row, rowIdx) => {
						const prevSection =
							rowIdx > 0 ? artifact.rows[rowIdx - 1].section : undefined;
						const showSectionHeader =
							!!row.section && row.section !== prevSection;
						return (
							<React.Fragment key={row.row_key}>
								{showSectionHeader && (
									<tr>
										<th
											scope="colgroup"
											colSpan={artifact.columns.length}
											className="border-b border-gray-200 bg-gray-50 px-3 pb-1 pt-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted"
										>
											{row.section}
										</th>
									</tr>
									)}
									<tr>
										{artifact.columns.map((col) => {
											if (col.kind === "dimension") {
												// Multi-dimension rows carry per-column text; fall
												// back to the row label for the single-dimension case.
												const dimText =
													row.dimension_values?.[col.column_key] ?? row.label;
												return (
													<td
														key={col.column_key}
														className="sticky left-0 z-10 whitespace-nowrap border-b border-gray-100 bg-white px-3 py-2 pr-8 font-medium text-text-base"
													>
														{dimText}
													</td>
												);
											}

											const cell = resolveCell(
												artifact,
												row.cells,
												col.column_key,
											);
											const presentation = presentCell(
												cell,
												MISSING_PLACEHOLDER,
												valueIndex,
											);

											return (
												<td
													key={col.column_key}
													className="whitespace-nowrap border-b border-gray-100 px-3 py-2 text-right tabular-nums text-text-base"
												>
													{presentation.kind === "excel" && (
														<>
															<CitedExcelCell
																cell={presentation.cell}
																chatId={chatId}
																className="markdown-doppler-link"
															/>
														</>
													)}
													{presentation.kind === "plain" && (
														<span
															className={
																presentation.unverified
																	? "inline-flex items-center gap-1 text-amber-700"
																	: undefined
															}
															title={
																presentation.unverified
																	? presentation.issue ?? "Unverified value"
																	: undefined
															}
														>
															{presentation.display}
															{presentation.unverified && (
																<AlertTriangle
																	className="h-3.5 w-3.5"
																	aria-label={
																		presentation.issue ?? "Unverified value"
																	}
																/>
															)}
														</span>
													)}
													{presentation.kind === "derived" && (
														<DerivedCellTrace
															presentation={presentation}
															chatId={chatId}
														/>
													)}
													{presentation.kind === "missing" && (
														<span className="text-text-muted">
															{presentation.placeholder || MISSING_PLACEHOLDER}
														</span>
													)}
												</td>
											);
										})}
									</tr>
								</React.Fragment>
							);
						})}
					</tbody>
				</table>

				<div className={`mt-1 text-xs ${COVERAGE_TONE[coverage.status]}`}>
					{(() => {
						const notPresent = coverage.not_present ?? 0;
						const accountable = Math.max(
							0,
							coverage.total_required - coverage.not_applicable - notPresent,
						);
						return (
							<>
								{COVERAGE_LABEL[coverage.status]} · {coverage.verified}/
								{accountable} verified
								{notPresent > 0 && `, ${notPresent} not present`}
								{coverage.missing > 0 && `, ${coverage.missing} missing`}
								{coverage.unverified > 0 &&
									`, ${coverage.unverified} unverified`}
							</>
						);
					})()}
				</div>

				{warnings.length > 0 && (
					<ul className="mt-1 space-y-0.5">
						{warnings.map((warning, idx) => (
							<li
								key={idx}
								className="flex items-start gap-1 text-xs text-amber-700"
							>
								<AlertTriangle className="mt-0.5 h-3 w-3 flex-shrink-0" />
								<span>{warning}</span>
							</li>
						))}
					</ul>
				)}
		</div>
	);

	if (!peekEnabled) return content;
	return (
		<EvidencePeekProvider chatId={chatId ?? undefined}>
			{content}
		</EvidencePeekProvider>
	);
};

export default CitedTableArtifactView;
